import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import type { HealthOverviewParameter, HealthWoundTrendPoint } from '@/domain/health';

import OverviewSectionHeader from './OverviewSectionHeader';
import ParameterRow from './ParameterRow';

import styles from './LaksvelOverviewChart.module.scss';

interface LaksvelOverviewChartProps {
    parameters: HealthOverviewParameter[];
    woundDailyTrend: HealthWoundTrendPoint[];
    getLabel: (key: string) => string;
    getDetailUrl: (key: string) => string | null;
    isLoading: boolean;
    /** A new range is in flight: keep the rows, and show the charts as loading. */
    isRefreshing?: boolean;
    isExportDisabled?: boolean;
}

export default function LaksvelOverviewChart(props: LaksvelOverviewChartProps) {
    const {
        parameters,
        woundDailyTrend,
        getLabel,
        getDetailUrl,
        isLoading,
        isRefreshing = false,
        isExportDisabled = false,
    } = props;
    const navigate = useNavigate();

    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    // Root of the "copy as image" capture: whatever is expanded on screen is what gets copied.
    const captureRef = useRef<HTMLDivElement>(null);
    const allExpanded =
        parameters.length > 0 && parameters.every((parameter) => expandedKeys.has(parameter.key));

    function toggleAll() {
        setExpandedKeys(
            allExpanded ? new Set() : new Set(parameters.map((parameter) => parameter.key))
        );
    }

    if (isLoading) {
        return (
            <div ref={captureRef}>
                <OverviewSectionHeader
                    parameters={[]}
                    woundDailyTrend={[]}
                    getLabel={getLabel}
                    captureRef={captureRef}
                    isExportDisabled
                    allExpanded={false}
                    onToggleAll={toggleAll}
                    isDateRangeDisabled
                />
                <div className={styles.skeletonWrapper}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonBox key={i} width="100%" height={44} borderRadius={8} />
                    ))}
                </div>
            </div>
        );
    }

    if (parameters.length === 0) {
        return null;
    }

    return (
        <div ref={captureRef}>
            <OverviewSectionHeader
                parameters={parameters}
                woundDailyTrend={woundDailyTrend}
                getLabel={getLabel}
                captureRef={captureRef}
                isExportDisabled={isExportDisabled}
                allExpanded={allExpanded}
                onToggleAll={toggleAll}
                isDateRangeDisabled={isRefreshing}
            />
            <div className={styles.list}>
                {parameters.map((parameter) => {
                    const label = getLabel(parameter.key);
                    const detailUrl = getDetailUrl(parameter.key);
                    const expanded = expandedKeys.has(parameter.key);

                    return (
                        <ParameterRow
                            key={parameter.key}
                            parameter={parameter}
                            label={label}
                            expanded={expanded}
                            isLoading={isRefreshing}
                            onToggle={() =>
                                setExpandedKeys((current) => {
                                    const next = new Set(current);
                                    if (next.has(parameter.key)) {
                                        next.delete(parameter.key);
                                    } else {
                                        next.add(parameter.key);
                                    }
                                    return next;
                                })
                            }
                            onNavigate={detailUrl ? () => navigate(detailUrl) : null}
                        />
                    );
                })}
            </div>
        </div>
    );
}
